import React, { useCallback, useRef, useState } from "react";

import { searchFieldsByProject } from "@synthesis/shared-services/project-gge/projectSearchController/client";
import { Button, Checkbox, ScrollArea, SearchBar } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import type { FilterFieldName, SearchDto } from "../projects-page.lib";

type CustomCheckboxFilterProps = {
  name: FilterFieldName;
  searchValue: null | string;
  searchPatternState: SearchDto | Record<string, never>;
  setSearchPatternState: React.Dispatch<React.SetStateAction<SearchDto | Record<string, never>>>;
};

export const CustomCheckboxFilter = ({ name, searchValue, searchPatternState, setSearchPatternState }: CustomCheckboxFilterProps) => {
  const [filterValues, setFilterValues] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [searchString, setSearchString] = React.useState("");
  const originalFilterValuesRef = useRef<string[]>([]);
  const [shouldClearSearch, setShouldClearSearch] = React.useState(false);

  const loadFilterValues = useCallback(async () => {
    try {
      const fieldMap = Object.entries(searchPatternState || {}).reduce<Record<string, string[]>>((acc, [key, val]) => {
        if (name !== key && val?.value) {
          acc[key] = val.value;
        }
        return acc;
      }, {});

      const newBody = searchValue ? { fieldMap, field: name, mainSearchField: searchValue } : { fieldMap, field: name };

      const filterFields = (await searchFieldsByProject({ body: newBody, query: { isArchive: false } })).data;
      originalFilterValuesRef.current = filterFields;
      setFilterValues(filterFields);
    } catch (e) {
      logger.error("Error loading filter values:", e);
    }
    setIsLoading(false);
  }, [name, searchPatternState, searchValue]);

  const handleCheckboxChange = (value: string, isChecked: boolean | string | undefined) => {
    if (isChecked) {
      setSelectedValues((prevValues) => [...prevValues, value]);
    } else {
      setSelectedValues((prevValues) => prevValues.filter((v) => v !== value));
    }
  };

  const applyFilters = () => {
    const searchPatterns: SearchDto = {};
    searchPatterns[name] = {
      value: selectedValues,
      pattern: "EQUALS",
    };
    setSearchPatternState((prevState) => ({
      ...prevState,
      ...searchPatterns,
    }));
  };

  const resetFilters = () => {
    setSelectedValues([]);
    setFilterValues(originalFilterValuesRef.current);
    setShouldClearSearch(true);
    setSearchPatternState((prevState) => {
      const newState = { ...prevState };
      delete newState[name];
      return newState;
    });
  };

  const onChangeSearch = (value: string) => {
    setShouldClearSearch(false);
    if (value === "") {
      setFilterValues(originalFilterValuesRef.current);
      return;
    }
    setSearchString(value);
    const newSearchValues = originalFilterValuesRef.current.filter((selectedValue) =>
      selectedValue.toLocaleLowerCase().includes(value.toLocaleLowerCase()),
    );
    setFilterValues(newSearchValues);
  };

  React.useEffect(() => {
    void loadFilterValues();
  }, [loadFilterValues]);

  React.useEffect(() => {
    if (searchPatternState[name]) {
      setSelectedValues([...(searchPatternState[name].value ?? [])]);
    }
    if (!searchPatternState[name]) {
      setSelectedValues([]);
    }
  }, [searchPatternState, name]);

  return (
    <div>
      <div className="px-4 py-2">
        <SearchBar clearable defaultValue={searchString} onChange={onChangeSearch} clearInput={shouldClearSearch} disabled={isLoading} />
      </div>
      <div className="relative">
        <div className="from-neutrals-white absolute bottom-0 left-0 right-0 z-50 h-4 w-full overflow-hidden bg-gradient-to-t to-transparent" />
        {isLoading ? (
          <figure className="bg-grey-300 h-12 w-full animate-pulse rounded bg-opacity-30" />
        ) : (
          <ScrollArea className="max-h-[280px] w-[262px] p-4 py-2">
            <div className="space-y-2">
              {filterValues.map((value) => (
                <Checkbox
                  key={value}
                  label={value}
                  name={value}
                  checked={selectedValues.includes(value)}
                  onChange={(e) => handleCheckboxChange(value, e)}
                />
              ))}
            </div>
          </ScrollArea>
        )}
        <div className="to-neutrals-white absolute left-0 right-0 top-0 h-4 w-full overflow-hidden bg-gradient-to-t from-transparent" />
      </div>
      <div className="flex justify-between p-2">
        <Button theme="primary" disabled={selectedValues.length < 1} onClick={applyFilters}>
          Применить
        </Button>
        <Button onClick={resetFilters}>Сбросить</Button>
      </div>
    </div>
  );
};
